import "./App.css";
import { createXRStore, XR } from "@react-three/xr";
import { Canvas } from "@react-three/fiber";
import { Sparkles } from "@react-three/drei";
import Loading from "./Loading.js";
import Spheres from "./Spheres.js";
import { Suspense, useState, useRef, useEffect, useMemo } from "react";

function App() {
  const store = createXRStore();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordings, setRecordings] = useState([]);
  const [showRecordings, setShowRecordings] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const destinationRef = useRef(null);

  // Memoize audio objects to prevent re-creation
  const audioObjects = useMemo(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const dest = ctx.createMediaStreamDestination();
    return { ctx, dest };
  }, []);

  // Initialize Web Audio API context only once
  useEffect(() => {
    audioContextRef.current = audioObjects.ctx;
    destinationRef.current = audioObjects.dest;
    setAudioReady(true);
    
    // Don't close the audio context - we need it for the entire session
    return () => {
      // Cleanup will happen when the component unmounts completely
    };
  }, [audioObjects]);

  const startRecording = async () => {
    try {
      if (!destinationRef.current) {
        alert('Audio system not ready. Please refresh the page.');
        return;
      }

      const stream = destinationRef.current.stream;
      
      // Check if stream has audio tracks
      if (stream.getAudioTracks().length === 0) {
        alert('No audio detected. Please play a sphere sound first, then start recording.');
        return;
      }

      // Use audio/webm as it's widely supported
      const options = { mimeType: 'audio/webm' };
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        const timestamp = new Date().toLocaleString();
        
        setRecordings(prev => [...prev, { 
          url, 
          timestamp, 
          duration: recordingTime 
        }]);
        
        clearInterval(timerRef.current);
        setRecordingTime(0);
      };

      mediaRecorder.start();
      setIsRecording(true);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error('Error starting recording:', err);
      alert('Could not start recording. Please try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const deleteRecording = (index) => {
    setRecordings(prev => {
      const newRecordings = [...prev];
      URL.revokeObjectURL(newRecordings[index].url);
      newRecordings.splice(index, 1);
      return newRecordings;
    });
  };

  const downloadRecording = (url, index) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `GORGEOUS-SPHERES-RECORDING-${index + 1}.webm`;
    a.click();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="App-frame">
        <header className="Header">
          <h1 className="Header-title">GORGEOUS SPHERES</h1>
          <p className="Header-text">CLICK THE SPHERES</p>
          <p className="Header-text-small">PLEASE HAVE SOUND ON</p>
          
          <button 
            className={`button-record ${isRecording ? 'recording' : ''}`}
            onClick={toggleRecording}
          >
            {isRecording ? `● Recording ${formatTime(recordingTime)}` : '● Record'}
          </button>
          
          {recordings.length > 0 && (
            <button 
              className="button-recordings"
              onClick={() => setShowRecordings(!showRecordings)}
            >
              {showRecordings ? 'Hide' : 'Show'} Recordings ({recordings.length})
            </button>
          )}
        </header>

        {showRecordings && recordings.length > 0 && (
          <div className="recordings-panel">
            <div className="recordings-header">
              <h3>Recordings</h3>
              <button
                className="close-button"
                onClick={() => setShowRecordings(false)}
              >
                ✕
              </button>
            </div>
            
            {recordings.map((rec, i) => (
              <div key={i} className="recording-item">
                <div className="recording-info">
                  <span>Recording {i + 1}</span>
                  <span>{formatTime(rec.duration)}</span>
                </div>
                <div className="recording-timestamp">
                  {rec.timestamp}
                </div>
                <audio 
                  controls 
                  src={rec.url} 
                  className="recording-audio"
                  controlsList="nodownload noplaybackrate"
                />
                <div className="recording-actions">
                  <button
                    className="download-button"
                    onClick={() => downloadRecording(rec.url, i)}
                  >
                    Download
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => deleteRecording(i)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="Canvas-frame">
          <Canvas flat className="Canvas" camera={{ fov: 50 }}>
            <XR store={store}>
              <Suspense
                fallback={<Loading position-y={0.05} scale={[2, 3, 2]} />}
              >
                {audioReady && (
                  <Spheres 
                    audioContext={audioObjects.ctx}
                    destination={audioObjects.dest}
                  />
                )}
              </Suspense>
              <Sparkles
                size={7}
                scale={[10, 10, 30]}
                position-y={1}
                speed={0.9}
                count={20}
                color="beige"
              />
              <Sparkles
                size={7}
                scale={[15, 20, 10]}
                position-y={0.9}
                speed={1}
                count={20}
                color="beige"
              />
            </XR>
          </Canvas>
        </div>
      </div>
    </>
  );
}

export default App;